import { ListConstructor } from "@/src/components/fetchableLists";
import { AppointmentType, frequentRoles, User } from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import {
  getItemId,
  getServiceId,
  getUserId,
  isReception,
  uid,
} from "@/src/utils/utils";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import CalendarUserCard from "./calendarUserCard";

export function CalendarUsersList({
  start,
  end,
  appointmentsTypes,
  current,
  onChange,
}: {
  start: string | null | undefined;
  end: string | null | undefined;
  appointmentsTypes: (AppointmentType | null | undefined)[] | null | undefined;
  current: string | null | undefined;
  onChange: (newUserId: string | null | undefined) => void;
}) {
  const uniqueId = useMemo(() => uid(), []);
  const [reload, setReload] = useState(0);
  const [selectedUser, setSelectedUser] = useState<string | null | undefined>(
    current
  );
  let [searchKey, setSearchKey] = useState("");
  let { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      "CalendarUsersList",
      reload,
      uniqueId,
      getUserId(),
      getServiceId(),
      JSON.stringify(appointmentsTypes),
      start,
      end,
    ],
    queryFn: async () =>
      apiFetch("users", "loadCalendarUsers", {
        serviceId: getServiceId(),
        isSecretary: isReception(USER.get()),
        userId: USER.get()?.id,
        appointmentTypesIds: appointmentsTypes?.map(
          (type) => getItemId(type?.id) || ""
        ),
        start,
        end,
      }),
  });

  const allUsers = [
    ...(data?.favouriteUsers || []),
    ...(data?.otherUsers || []),
  ];

  function containsSearchKey(user: User) {
    return `${user.firstName} ${user.lastName} ${user.firstName}`
      .toLowerCase()
      .trim()
      .includes(searchKey.toLowerCase().trim());
  }

  return (
    <div className="p10 scroll-y" style={{ height: "100%" }}>
      <ListConstructor
        data={allUsers}
        isLoading={isLoading}
        isError={isError}
        error={error}
        searchKey={searchKey}
        onSearch={(newValue) => {
          setSearchKey(newValue || "");
        }}
      >
        <div className={`usersList grid c1 mc1 gap20`}>
          <div className="c1 gap10">
            <div className="bold">Favoris</div>
            {data?.favouriteUsers?.map((user) => {
              if (!containsSearchKey(user)) return null;
              return (
                <CalendarUserCard
                  key={uid()}
                  user={user}
                  isFavourite={true}
                  isSelected={getItemId(selectedUser) === getItemId(user)}
                  onFavouriteChange={() => {
                    setReload(reload + 1);
                  }}
                  appointmentsCount={
                    data?.appointmentsCounts[getItemId(user)] || {}
                  }
                  onSelect={() => {
                    onChange(getItemId(user));
                    setSelectedUser(getItemId(user));
                  }}
                />
              );
            })}
            <CalendarUserCard
              key={uid()}
              user={{
                id: null,
                firstName: "",
                lastName: "Rendez-vous non assignés",
                role: frequentRoles.reception.id as any,
                address: "",
                creator: null,
                profilePic: null,
                grade: "Dr",
                patientId: null,
                interdictions: [],
                appointmentTypes: [],
                canScheduleTypes: [],
                canValidateTypes: [],
                sex: "homme",
                email: "",
                token: null,
                lastTokenDate: null,
                phoneNumber: "",
                lastOnlineDate: "",
                password: "",
                permissions: [],
                favouriteCalendars: [],
                preferredBox: null,
                remedLicence: null,
                isValidated: false,
                speciality: null,
              }}
              isFavourite={true}
              isSelected={getItemId(selectedUser) === null}
              onFavouriteChange={() => {
                setReload(reload + 1);
              }}
              appointmentsCount={data?.appointmentsCounts[null as any] || {}}
              onSelect={() => {
                onChange(null);
                setSelectedUser(null);
              }}
            />
          </div>

          <div className="c1 gap10">
            <div className="bold">Autres utilisateurs</div>
            {data?.otherUsers?.map((user) => {
              if (!containsSearchKey(user)) return null;
              return (
                <CalendarUserCard
                  key={uid()}
                  user={user}
                  isFavourite={false}
                  isSelected={getItemId(selectedUser) === getItemId(user)}
                  onFavouriteChange={() => {
                    setReload(reload + 1);
                  }}
                  appointmentsCount={
                    data?.appointmentsCounts[getItemId(user)] || {}
                  }
                  onSelect={() => {
                    onChange(getItemId(user));
                    setSelectedUser(getItemId(user));
                  }}
                />
              );
            })}
          </div>
        </div>
      </ListConstructor>
    </div>
  );
}
